import { useQuery } from '@tanstack/react-query';

import { getSitesBackendMode, getSitesSource } from './source';
import type { SiteListFilters } from './types';

const SITE_COMPLIANCE_SITES_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  retry: 1,
  staleTime: 30_000,
};

const normalizeFilters = (filters: SiteListFilters = {}) => ({
  businessUnit: filters.businessUnit || undefined,
  cursor: filters.cursor || undefined,
  datacenter: filters.datacenter,
  enhanced: filters.enhanced,
  flagship: filters.flagship,
  networkTier: filters.networkTier || undefined,
  pageSize: filters.pageSize,
  primaryDivision: filters.primaryDivision || undefined,
  region: filters.region || undefined,
  search: filters.search || undefined,
  siteId: filters.siteId || undefined,
  siteName: filters.siteName || undefined,
  siteType: filters.siteType || undefined,
  workdayId: filters.workdayId || undefined,
});

export const useSitesQuery = (filters: SiteListFilters = {}) => {
  const normalizedFilters = normalizeFilters(filters);
  const backendMode = getSitesBackendMode();

  return useQuery({
    queryFn: () => getSitesSource().listSites(normalizedFilters),
    queryKey: ['site-compliance', 'sites', backendMode, normalizedFilters],
    ...SITE_COMPLIANCE_SITES_QUERY_OPTIONS,
  });
};

export const useSiteDetailQuery = (siteId: string | null | undefined) => {
  const backendMode = getSitesBackendMode();

  return useQuery({
    enabled: Boolean(siteId),
    queryFn: () => getSitesSource().getSiteDetail(siteId ?? ''),
    queryKey: ['site-compliance', 'site-detail', backendMode, siteId],
    ...SITE_COMPLIANCE_SITES_QUERY_OPTIONS,
  });
};

export const useSiteResourcesQuery = (siteId: string | null | undefined) => {
  const backendMode = getSitesBackendMode();

  return useQuery({
    enabled: Boolean(siteId),
    queryFn: () => getSitesSource().getSiteResources(siteId ?? ''),
    queryKey: ['site-compliance', 'site-resources', backendMode, siteId],
    ...SITE_COMPLIANCE_SITES_QUERY_OPTIONS,
  });
};

export const useSiteConfigurationQuery = (
  siteId: string | null | undefined,
) => {
  const backendMode = getSitesBackendMode();

  return useQuery({
    enabled: Boolean(siteId),
    queryFn: () => getSitesSource().getSiteConfiguration(siteId ?? ''),
    queryKey: ['site-compliance', 'site-configuration', backendMode, siteId],
    ...SITE_COMPLIANCE_SITES_QUERY_OPTIONS,
  });
};
